import { Fragment } from "react";
import type { LookView, ThingView, ThreadView } from "../../shared/story";
import { Credit, TrustLinks } from "../trust";
import { WhereYouAre } from "../noirmap";
import { Care, Decision, VoiceShift, DeleteStory, Kept, SceneBreak, Weaving, waiting, More, SoundToggle, ChapterEnd, DreamNow, HurryHint, frayOf, inkOf, Plates, PlatesLink, Returned, SkinPicker, Unwoven, ReadingRoom, holdToHurry, Writer, linkify, quietFor, threadOrder, type ThemeProps } from "../common";
import { Margins } from "../v2/margins";
import { PenLine, Pull } from "../v2/pens";
import { LookActions } from "../v2/pocket";
import type { Shown, StoryUI } from "../useStory";
import "./cyanotype.css";

/**
 * Cyanotype: the story printed by light (owner's pick, 2026-10-18; mock-up
 * https://claude.ai/artifact/LA1ArVrSHh3ihtdBmRZWWG). Prussian blue paper, the narrator's prose exposed white on it;
 * your lines are strips of unexposed paper laid on the print; plates are blue sun-prints; the open threads are
 * specimens with numbered tags; a chapter ends when the print is fixed.
 */

const QUIET = 3;

function Strip({ t, meta }: { t: { input: string; kept?: string[] }; meta: boolean }) {
  return (
    <div className={`cy-strip${meta ? " cy-strip-aside" : ""}`}>
      <span className="cy-label">{meta ? "to the darkroom" : "you"}</span>
      <p><PenLine input={t.input} /></p>
      <Kept t={t} className="cy-kept" />
    </div>
  );
}

function Specimen({ s, l, onLook }: { s: StoryUI; l: LookView; onLook(name: string): void }) {
  return (
    <aside className="cy-specimen" aria-label={`Looked closer: ${l.thing}`}>
      <span className="cy-label">specimen · {l.thing}</span>
      <p>{l.description}</p>
      <p className="cy-specimen-kids">
        <span className="cy-label">closer:</span>{" "}
        {l.children.map((ch) => (
          <button key={ch.id} type="button" className="lm-thing" title={ch.hint} onClick={() => onLook(ch.name)}>{ch.name}</button>
        ))}
      </p>
      <LookActions s={s} thing={l.thing} className="cy-label" />
    </aside>
  );
}

function Exposure({ s, t, things, looks, onLook, live }: {
  s: StoryUI; t: Shown; things: readonly ThingView[]; looks?: LookView[]; onLook: ((name: string) => void) | null;
  live?: { streaming: boolean; paused: boolean };
}) {
  const meta = t.intent === "meta";
  return (
    <article className="cy-turn" data-turn={t.n}>
      <SceneBreak t={t} className="cy-break" mark="· · ·" />
      <Strip t={t} meta={meta} />
      <div className={`cy-prose${meta ? " cy-meta" : ""}`} {...(live ? holdToHurry(s) : {})}>
        <Returned t={t} className="cy-return" />
        <VoiceShift t={t} className="cy-voice-shift" />
        <Decision t={t} className="cy-decision" />
        <p data-prose={meta ? undefined : t.n} {...(live ? { "data-live": "" } : {})}>
          {meta && <span className="cy-label">darkroom · </span>}
          {linkify(t.prose, things, meta ? null : onLook, "lm-thing", inkOf(s.view, t.n))}
          {t.status === "cut" && <span className="cy-torn" title="you cut in here" aria-label="cut here"> ⌇</span>}
          <Unwoven s={s} t={t} label={(n) => `${n} ${n === 1 ? "word" : "words"} left unexposed`} />
          {live?.streaming && <span className="cy-caret" aria-hidden="true" />}
        </p>
        {live?.paused && <p className="cy-held">the light holds while you type · enter cuts in here</p>}
        {live && <HurryHint s={s} className="cy-held" />}
        {looks?.map((l) => onLook && <Specimen key={l.thing} s={s} l={l} onLook={onLook} />)}
        {!meta && !live && <Pull s={s} n={t.n} className="cy-pull" />}
        {!meta && !live && <Margins s={s} n={t.n} className="cy-margins" />}
        <ChapterEnd s={s} t={t} className="cy-chapter" />
      </div>
    </article>
  );
}

/** The open threads as specimen tags, numbered in the order they appeared; tap one to bring it back. */
function Specimens({ s, threads, now, className = "" }: { s: StoryUI; threads: readonly ThreadView[]; now: number; className?: string }) {
  const order = [...threads].sort((a, b) => a.turn - b.turn || a.id.localeCompare(b.id));
  const shown = threadOrder(threads).filter((t) => t.state === "open").slice(0, 6);
  if (!shown.length) return null;
  return (
    <ul className={`cy-tags ${className}`} aria-label="Threads: tap one to bring it back on your next line">
      {shown.map((t) => {
        const quiet = quietFor(t, now) >= QUIET;
        const no = order.findIndex((x) => x.id === t.id) + 1;
        const tag = <><span className="cy-no">No. {no}</span> {t.name}</>;
        return (
          <li key={t.id} title={t.hint}>
            {s.id
              ? <button type="button" className={`lm-pull${quiet ? " cy-faded" : ""}`} data-fray={frayOf(t, now)} onClick={() => s.pull(t)} disabled={s.busy} title={`Bring it back: ${t.hint}`}>{tag}</button>
              : <span>{tag}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function Arrow() {
  return <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 10h11M11 5l5 5-5 5" /></svg>;
}

export function CyanotypeTheme({ s, home, skin, setSkin, goHome, openPlates, meta }: ThemeProps) {
  const view = s.view;
  const threads = view?.threads ?? [];
  const things = view?.things ?? [];
  const lookAt = (n: number) => (name: string) => s.look(n, name);

  const now = view?.n ?? s.live?.n ?? 0;
  const starting = !!home && !s.pending;
  const placeholder = s.live?.streaming ? "typing holds the light; enter cuts in" : s.id ? "What do you do?" : "Begin your story…";

  return (
    <main className="sk-cyanotype">
      <div className="cy-frame">
        <div className="cy-main">
          <header className="cy-head">
            <a className="cy-brand" href="/" aria-label={s.id ? "Your stories" : undefined} onClick={(e) => { e.preventDefault(); if (s.id) meta("stories"); else goHome(); }}>Weftwake{s.id && <span className="lm-brand-caret" aria-hidden="true"> ▾</span>}</a>
            <span className="cy-title">{view ? <>{view.title}{now ? <> · turn {now}</> : null}</> : s.live ? `turn ${s.live.n}` : ""}</span>
            <SoundToggle className="cy-sound" />
            <SkinPicker skin={skin} setSkin={setSkin} className="cy-skins" />
            {view && <Specimens s={s} threads={threads} now={now} className="cy-phone-only" />}
          </header>

          {starting && (
            <section className="cy-home">
              <p className="cy-lede">Blank paper, waiting for light. Write what you do, where you are, what's true; the narrator exposes what happens next.</p>
              <p className="lm-what">Weftwake is a text adventure that writes itself as you play: you write what you do, an AI narrator writes what happens. Your first 40 turns are free.</p>
              <div className="cy-seeds" aria-label="Or begin with">
                {home.seeds.map((o) => (
                  <button key={o} type="button" onClick={() => s.send(o)} disabled={s.busy}>{o}</button>
                ))}
              </div>
            </section>
          )}

          <div className="cy-print" role="log" aria-live="polite" aria-label="The story" aria-busy={!!s.live?.streaming || !!s.pending}>
            {view?.recap && s.turns.length > 0 && s.turns[0]!.n > 1 && (
              <p className="cy-recap cy-phone-only"><span className="cy-label">previously</span> {view.recap}</p>
            )}
            {s.loadFailed && (
              <p className="cy-notice">Couldn't open this story. <button type="button" onClick={s.reload}>Try again</button></p>
            )}
            {s.turns.map((t) => (
              <Fragment key={t.n}>
                <Exposure s={s} t={t} things={things} looks={s.looks[t.n]} onLook={s.id ? lookAt(t.n) : null} />
                <Plates s={s} at={t.n} className="cy-plate" />
              </Fragment>
            ))}
            {s.live && (
              <Exposure
                s={s}
                t={{ n: s.live.n, input: s.live.input, prose: s.live.text, status: "streaming", intent: s.live.intent }}
                things={[]} onLook={null}
                live={{ streaming: s.live.streaming, paused: s.live.paused }}
              />
            )}
            {s.pending && !s.live && (
              <article className="cy-turn" data-pending><Strip t={{ input: s.pending }} meta={false} /></article>
            )}
            {waiting(s) && <Weaving label="exposing…" className="cy-weaving" />}
            {/* The room the reply writes into (common.tsx ReadingRoom): nothing on screen moves while it's written. */}
            {s.id && <ReadingRoom s={s} writer=".cy-writer" />}
          </div>

          <Care s={s} className="cy-care" />
          {s.notice && <p className="cy-notice" role="status">{s.notice}</p>}

          {!s.resources && (
            <Writer
              s={s}
              className="cy-writer"
              label={s.id ? "Your next move" : "The first line of your story"}
              placeholder={placeholder}
              sendLabel={<Arrow />}
            />
          )}

          {starting && home.stories.length > 0 && (
            <section className="cy-shelf" aria-label="Your stories">
              <h2>Your stories</h2>
              <ul>
                {home.stories.map((st) => (
                  <li key={st.id}>
                    <a href={`/s/${st.id}`} onClick={(e) => { e.preventDefault(); home.open(st.id); }}>{st.title}</a>
                    <span>{st.n} {st.n === 1 ? "turn" : "turns"}</span>
                    <DeleteStory title={st.title} remove={() => home.remove(st.id)} />
                  </li>
                ))}
              </ul>
              <p className="cy-label"><PlatesLink open={openPlates} label="your plates →" /></p>
            </section>
          )}

          {starting && <footer className="cy-foot cy-home-foot"><TrustLinks meta={meta} /></footer>}
          {starting && <Credit className="cy-credit" />}

          {s.id && (
            <footer className="cy-foot">
              <PlatesLink open={openPlates} />
              <button type="button" onClick={() => meta("stories")}>stories</button>
              <button type="button" onClick={() => meta("map")}>journal</button>
              <button type="button" onClick={() => meta("cloth")}>the cloth</button>
              <button type="button" onClick={() => meta("account")}>account</button>
              <button type="button" onClick={() => meta("feedback")}>feedback</button>
              <More>
                <a href="/privacy" onClick={(e) => { e.preventDefault(); meta("privacy"); }}>privacy</a>
                <a href="/api/export">export</a>
                <button type="button" onClick={() => meta("tour")}>how to play</button>
                <DreamNow s={s} />
                <button type="button" onClick={s.remove}>delete this story</button>
              </More>
            </footer>
          )}
        </div>

        {view && (
          <aside className="cy-side" aria-label="Beside the story">
            <WhereYouAre view={view} onLook={(name) => { const n = s.turns.at(-1)?.n; if (n) s.look(n, name); }} className="cy-where" />
            {threads.some((t) => t.state === "open") && (
              <section>
                <h2>Specimens</h2>
                <Specimens s={s} threads={threads} now={now} className="cy-tags-side" />
              </section>
            )}
            {view.recap && (
              <section>
                <h2>Previously</h2>
                <p className="cy-side-recap">{view.recap}</p>
              </section>
            )}
          </aside>
        )}
      </div>
    </main>
  );
}
